import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

/** Body for POST and PATCH. Mirrors backend sanitizeTransactionInput's expectations. */
export function cleanInput(input = {}) {
    const paidBy = input.type === 'income' ? 'club' : (input.paidBy || 'club');
    const cleaned = {
        type: String(input.type || '').trim(),
        date: String(input.date || '').trim(),
        amount: parseFloat(String(input.amount).replace(',', '.')) || 0,
        category: String(input.category || '').trim(),
        description: String(input.description == null ? '' : input.description).trim(),
        paidBy,
        paidByUser: paidBy === 'member' ? (parseInt(input.paidByUser, 10) || 0) : 0,
        images: Array.isArray(input.images) ? input.images : [],
    };
    if (input.customer != null) {
        cleaned.customer = parseInt(input.customer, 10) || 0;
    }
    return cleaned;
}

class ClubTransactionStore extends LocalModel {
    constructor() {
        super('clubTransaction', {
            namePlural: 'clubTransactions',
            namePluralReal: 'clubtransactions',
            sort: '-date',
            limit: 1000,
            api: {
                search: { url: '/api/clubtransactions/', params: {} },
                load: { url: '/api/clubtransactions/', params: {} },
                save: { url: '/api/clubtransactions/', params: {} },
                delete: { url: '/api/clubtransactions/', params: {} },
            },
        });
    }

    @observable clubTransactions = [];

    @observable clubTransaction = {};

    @observable summary = {};

    @observable isLoading = false;

    @observable lastError = '';

    @action
    async loadYear(year, filters = {}) {
        this.updateKeyValue('isLoading', true);
        const params = { year };
        if (filters.type) params.type = filters.type;
        if (filters.category) params.category = filters.category;
        if (filters.outstanding) params.outstanding = 1;
        const response = await util.fetchApi('/api/clubtransactions/', { method: 'GET' }, params);
        if (response?.status === 200) {
            this.updateKeyValue('clubTransactions', response.data || []);
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }

    /** Outstanding member-paid entries of a year, without touching the list observable. */
    @action
    async fetchOutstanding(year) {
        const response = await util.fetchApi('/api/clubtransactions/', { method: 'GET' }, { year, outstanding: 1 });
        return response?.status === 200 ? (response.data || []) : [];
    }

    /** Rendered accountant mail for one expense (nothing is sent). Returns data or null. */
    @action
    async emailPreview(id, message = '') {
        const response = await util.fetchApi(`/api/clubtransactions/${id}/email-preview`, { method: 'GET' }, {
            message: String(message || '').trim(),
        });
        return response?.status === 200 ? (response.data || null) : null;
    }

    @action
    async loadSummary(year) {
        const response = await util.fetchApi('/api/clubtransactions/summary', { method: 'GET' }, { year });
        if (response?.status === 200) {
            this.updateKeyValue('summary', response.data || {});
        }
        return response;
    }

    @action
    async add(input) {
        const response = await util.fetchApi('/api/clubtransactions/', { method: 'POST' }, cleanInput(input));
        return this.afterWrite(response);
    }

    @action
    async update(id, input) {
        const response = await util.fetchApi(`/api/clubtransactions/${id}`, { method: 'PATCH' }, cleanInput(input));
        return this.afterWrite(response);
    }

    @action
    async reimburse(id, { date, undo } = {}) {
        const body = undo ? { undo: 1 } : { date };
        const response = await util.fetchApi(`/api/clubtransactions/${id}/reimburse`, { method: 'POST' }, body);
        return this.afterWrite(response);
    }

    @action
    async remove(id) {
        const response = await util.fetchApi(`/api/clubtransactions/${id}`, { method: 'DELETE' }, {});
        return this.afterWrite(response);
    }

    /** Officer fills in the bank account of the member who paid this expense. */
    @action
    async setPayerBankAccount(id, bankAccount) {
        const body = { bankAccount: String(bankAccount || '').trim() };
        const response = await util.fetchApi(`/api/clubtransactions/${id}/payer-bank-account`, { method: 'PATCH' }, body);
        return this.afterWrite(response);
    }

    /** E-mail one expense with receipts to a typed address (accountant). Logged server-side. */
    @action
    async emailToAccountant(id, { to, cc, message } = {}) {
        const body = {
            to: String(to || '').trim(),
            cc: String(cc || '').trim(),
            message: String(message || '').trim(),
        };
        const response = await util.fetchApi(`/api/clubtransactions/${id}/email`, { method: 'POST' }, body);
        return this.afterWrite(response);
    }

    @action
    async syncAmmoPurchases() {
        const response = await util.fetchApi('/api/clubtransactions/sync-ammo-purchases', { method: 'POST' }, {});
        return response;
    }

    @action
    afterWrite(response) {
        if (response?.status === 201 || response?.status === 202) {
            this.updateKeyValue('lastError', '');
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not save');
        }
        return response;
    }
}

const store = new ClubTransactionStore();
export default store;
